import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

export class AdmissionCategoryChangeUtils {
    static reFormUsersObj = (users) => {
        return users.map(user => {
            if (user.user_info) {
                return { ...user, ...user.user_info };
            }
            return user;
        });
    };

    static removeOuterDuplicates = (obj, nestedKey = 'user_info') => {
        if (!obj[nestedKey]) return obj;
        
        const nested = obj[nestedKey];
        const cleaned = { ...obj };
        
        Object.keys(nested).forEach(key => {
            if (cleaned.hasOwnProperty(key)) {
                delete cleaned[key];
            }
        });
        
        return cleaned;
    };

    static capitalizeFirstLetter = (string) => {
        if (!string) return '';
        return string.split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    static getValidDate = (dateValue) => {
        if (!dateValue) return null;
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    };

    static sortStepTwoData = (data) => {
        return [...data].sort((a, b) => {
            if (a.priority !== b.priority) {
                return a.priority - b.priority;
            }
            return a.term - b.term;
        });
    };

    static getInstallmentIdForDate = (dueDate, selectedFeePlan, oneTimeFees = []) => {
        if (!selectedFeePlan?.installments) {
            return uuidv4();
        }

        // First check if this date matches any existing installment dates
        const matchingInstallment = selectedFeePlan.installments.find(inst =>
            inst?.date && moment(inst.date).format('YYYY-MM-DD') === moment(dueDate).format('YYYY-MM-DD')
        );

        if (matchingInstallment) {
            return matchingInstallment.installmentId;
        }

        // If no matching installment found, check if other one-time fees have this date
        const oneTimeFeesGrouped = {};
        oneTimeFees?.forEach(fee => {
            if (!fee?.dueDate) return;

            const dateKey = moment(fee.dueDate).format('YYYY-MM-DD');
            if (!oneTimeFeesGrouped[dateKey]) {
                oneTimeFeesGrouped[dateKey] = {
                    installmentId: fee.installmentId || uuidv4(),
                    fees: []
                };
            }
            oneTimeFeesGrouped[dateKey].fees.push(fee);
        });

        const dateKey = moment(dueDate).format('YYYY-MM-DD');
        return oneTimeFeesGrouped[dateKey]?.installmentId || uuidv4();
    };

    static calculateTotalEnteredAmount = (feeType, installmentData = []) => {
        let totalEntered = 0;

        installmentData.forEach(installment => {
            const matchingFee = installment.feeTypes?.find(
                fee => fee.feeType === feeType.feeType && fee.feeTypeId === feeType.feeTypeId
            );

            if (matchingFee && matchingFee.calculatedFee) {
                totalEntered += Number(matchingFee.calculatedFee);
            }
        });

        return totalEntered;
    };

    static getAmountColor = (enteredAmount, totalAmount) => {
        if (enteredAmount === 0) return "#000000";
        if (enteredAmount === Number(totalAmount)) return "#2196F3";
        return "#FF0000";
    };

    static validateInstallmentPercentages = (installmentData, showToast) => {
        let feeTypePercentageMap = {};
        let isValid = true;

        for (const installment of installmentData) {
            for (const feeType of installment.feeTypes) {
                if (feeType.percentage > 100) {
                    showToast('error', 'Error', `Percentage for ${feeType.feeType} cannot exceed 100%`);
                    isValid = false;
                }
                if (feeType.percentage < 0) {
                    showToast('error', 'Error', `Percentage for ${feeType.feeType} cannot be negative`);
                    isValid = false;
                }
                if (feeType.percentage > 0) {
                    feeTypePercentageMap[feeType.feeType] = (feeTypePercentageMap[feeType.feeType] || 0) + feeType.percentage;
                }
            }
        }

        if (Object.keys(feeTypePercentageMap).length === 0) {
            showToast('error', 'Error', 'Enter all fee types with non-zero percentages');
            isValid = false;
        }

        for (const [feeType, totalPercentage] of Object.entries(feeTypePercentageMap)) {
            if (totalPercentage !== 100) {
                showToast('error', 'Error', `Total percentage for fee type ${feeType} must be exactly 100%`);
                isValid = false;
            }
        }

        return isValid;
    };

    static processStepOneData = (selectedFeeStructure, oneTimeFees = []) => {
        // Handle array structure - get unique fee types with highest totalFee
        let allFeeTypes = [];
        if (Array.isArray(selectedFeeStructure)) {
            allFeeTypes = selectedFeeStructure.reduce((acc, category) => {
                if (Array.isArray(category?.feeTypes)) {
                    acc.push(...category.feeTypes);
                }
                return acc;
            }, []);
        }

        // Create a map to store unique fee types with highest totalFee
        const feeTypeMap = new Map();
        allFeeTypes.forEach(feeType => {
            if (!feeType?._id) return;
            
            const existingFeeType = feeTypeMap.get(feeType._id);
            if (!existingFeeType || (feeType.totalFee || 0) > (existingFeeType.totalFee || 0)) {
                feeTypeMap.set(feeType._id, feeType);
            }
        });

        const uniqueFeeTypes = Array.from(feeTypeMap.values());

        return uniqueFeeTypes?.map((feeType, index) => {
            const feeTypeId = feeType._id ?? feeType?.feeTypeId;

            const oneTimeFee = oneTimeFees?.find(otf => {
                const otfId = otf._id ?? otf.feeTypeId;
                return otfId === feeTypeId;
            });

            return {
                feeType: feeType.feeType,
                admissionType: feeType.admissionType || [],
                feeTypeId: feeTypeId,
                admissionTypes: feeType.admissionTypes || [],
                priority: index + 1,
                totalFee: oneTimeFee ? Number(oneTimeFee.totalFee) : Number(feeType.totalFee)
            };
        });
    };

    static processStepTwoData = (installmentData, oneTimeFees = []) => {
        let stepTwo = [];

        // Add regular installment fees
        installmentData.forEach((installment, installmentIndex) => {
            installment.feeTypes.forEach(feeType => {
                stepTwo.push({
                    feeType: feeType.feeType,
                    feeTypeId: feeType._id ?? feeType?.feeTypeId,
                    term: installmentIndex + 1,
                    paymentTerm: `Installment ${installmentIndex + 1}`,
                    totalFee: Number(feeType.totalFee),
                    isDifferentialFee: feeType.isDifferentialFee || false,
                    isTransportFeeType: feeType.isTransportFeeType || false,
                    admissionType: feeType.admissionType || [],
                    priority: feeType.priority || 1,
                    termWisePercentageOrValue: feeType.percentage,
                    termWiseActualValue: feeType.calculatedFee,
                    installmentId: installment.installmentId,
                    dueDate: installment.dueDate,
                    isOneTime: false
                });
            });
        });

        // Add one-time fees
        oneTimeFees.forEach(feeType => {
            const existingInstallment = stepTwo.find(fee => 
                fee.dueDate && fee.isOneTime && moment(fee.dueDate).isSame(feeType.dueDate, 'day')
            );

            const installmentId = existingInstallment ? existingInstallment.installmentId : uuidv4();

            oneTimeFees.forEach(fee => {
                if (moment(fee.dueDate).isSame(feeType.dueDate, 'day')) {
                    fee.installmentId = installmentId;
                }
            });

            stepTwo.push({
                feeType: feeType.feeType,
                feeTypeId: feeType._id ?? feeType?.feeTypeId,
                isOneTime: true,
                paymentTerm: "One Time",
                totalFee: feeType.totalFee,
                isDifferentialFee: feeType.isDifferentialFee || false,
                isTransportFeeType: feeType.isTransportFeeType || false,
                admissionType: feeType.admissionType || [],
                priority: feeType.priority,
                termWisePercentageOrValue: 100,
                termWiseActualValue: feeType.totalFee,
                installmentId: installmentId,
                dueDate: feeType.dueDate,
                term: 1
            });
        });

        return stepTwo;
    };
}
